import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Code, Rocket, Zap, Github, Linkedin, Mail, Download, GraduationCap, Instagram, Landmark } from 'lucide-react';
import StructuredData from '../components/StructuredData';
import ImageStructuredData from '../components/ImageStructuredData';
import useScrollReveal from '../hooks/useScrollReveal';

function Home() {
  /* Removed LCP-blocking animations */
  const [statsRef, statsVisible] = useScrollReveal({ threshold: 0.2 });
  const [techRef, techVisible] = useScrollReveal({ threshold: 0.1 });
  const [featuresRef, featuresVisible] = useScrollReveal({ threshold: 0.1 });

  const skills = [
    { name: 'React.js', level: 95 },
    { name: 'Node.js', level: 90 },
    { name: 'MongoDB', level: 85 },
    { name: 'TypeScript', level: 88 }
  ];

  const stats = [
    { icon: Code, value: '10+', label: 'Projects Completed' },
    { icon: Landmark, value: 'SLIIT', label: 'Sri Lanka Institute of Information Technology' },
    { icon: GraduationCap, value: 'Undergraduate', label: 'BSc in Information Technology' }
  ];

  const developmentStack = [
    {
      name: 'HTML5',
      iconImage: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/html5/html5-original.svg',
      color: 'from-orange-500 to-red-500'
    },
    {
      name: 'CSS3',
      iconImage: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/css3/css3-original.svg',
      color: 'from-blue-400 to-blue-600'
    },
    {
      name: 'JavaScript',
      iconImage: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/javascript/javascript-original.svg',
      color: 'from-cyan-400 to-blue-500'
    },
    {
      name: 'TypeScript',
      iconImage: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/typescript/typescript-original.svg',
      color: 'from-blue-500 to-blue-700'
    },
    {
      name: 'PHP',
      iconImage: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/php/php-original.svg',
      color: 'from-indigo-400 to-indigo-600'
    },
    {
      name: 'TailwindCSS',
      iconImage: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/tailwindcss/tailwindcss-original.svg',
      color: 'from-blue-600 to-indigo-600'
    },
    {
      name: 'React.js',
      iconImage: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/react/react-original.svg',
      color: 'from-blue-400 to-cyan-400'
    },
    {
      name: 'Angular',
      iconImage: 'https://cdn.simpleicons.org/angular/DD0031',
      color: 'from-red-500 to-red-700'
    },
    {
      name: 'Next.js',
      iconImage: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/nextjs/nextjs-original.svg',
      color: 'from-gray-800 to-black'
    },
    {
      name: 'Node.js',
      iconImage: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/nodejs/nodejs-original.svg',
      color: 'from-green-400 to-emerald-400'
    },
    {
      name: 'Express.js',
      iconImage: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/express/express-original.svg',
      color: 'from-gray-400 to-gray-600'
    },
    {
      name: 'Spring Boot',
      iconImage: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/spring/spring-original.svg',
      color: 'from-green-500 to-green-700'
    },
    {
      name: 'MySQL',
      iconImage: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/mysql/mysql-original.svg',
      color: 'from-blue-500 to-blue-800'
    },
    {
      name: 'MongoDB',
      iconImage: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/mongodb/mongodb-original.svg',
      color: 'from-green-500 to-green-600'
    }
  ];

  const devopsStack = [
    {
      name: 'Linux',
      iconImage: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/linux/linux-original.svg',
      color: 'from-yellow-400 to-yellow-600'
    },
    {
      name: 'Git',
      iconImage: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/git/git-original.svg',
      color: 'from-orange-500 to-red-500'
    },
    {
      name: 'GitHub',
      iconImage: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/github/github-original.svg',
      color: 'from-gray-700 to-gray-900'
    },
    {
      name: 'GitHub Actions',
      iconImage: 'https://cdn.simpleicons.org/githubactions/2088FF',
      color: 'from-blue-500 to-blue-700'
    },
    {
      name: 'Jenkins',
      iconImage: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/jenkins/jenkins-original.svg',
      color: 'from-red-500 to-red-700'
    },
    {
      name: 'Docker',
      iconImage: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/docker/docker-original.svg',
      color: 'from-blue-500 to-blue-700'
    },
    {
      name: 'Kubernetes',
      iconImage: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/kubernetes/kubernetes-plain.svg',
      color: 'from-blue-600 to-blue-800'
    },
    {
      name: 'Terraform',
      iconImage: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/terraform/terraform-original.svg',
      color: 'from-purple-500 to-indigo-600'
    },
    {
      name: 'Ansible',
      iconImage: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/ansible/ansible-original.svg',
      color: 'from-gray-600 to-gray-800'
    },
    {
      name: 'Prometheus',
      iconImage: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/prometheus/prometheus-original.svg',
      color: 'from-orange-500 to-red-500'
    },
    {
      name: 'Grafana',
      iconImage: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/grafana/grafana-original.svg',
      color: 'from-orange-400 to-orange-600'
    },
    {
      name: 'AWS',
      iconImage: 'https://upload.wikimedia.org/wikipedia/commons/9/93/Amazon_Web_Services_Logo.svg',
      color: 'from-orange-400 to-yellow-500'
    },
    {
      name: 'Azure',
      iconImage: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/azure/azure-original.svg',
      color: 'from-blue-400 to-blue-600'
    },
    {
      name: 'GCP',
      iconImage: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/googlecloud/googlecloud-original.svg',
      color: 'from-blue-400 to-blue-600'
    },
    {
      name: 'Vercel',
      iconImage: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/vercel/vercel-original.svg',
      color: 'from-gray-800 to-black'
    },
  ];

  const features = [
    {
      icon: Code,
      title: 'Clean Code',
      description: 'Writing maintainable, scalable, and efficient code following best practices.'
    },
    {
      icon: Rocket,
      title: 'Fast Performance',
      description: 'Optimized applications with lightning-fast load times and smooth user experience.'
    },
    {
      icon: Zap,
      title: 'Modern Tech',
      description: 'Leveraging cutting-edge technologies to build innovative solutions.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Home | MrRandiv</title>
        <meta
          name="description"
          content="BSc in IT student at SLIIT passionate about full-stack web development and DevOps practices. Building expertise in modern web technologies and cloud infrastructure through coursework and hands-on projects. Eager to contribute to real-world projects."
        />
        <meta name="keywords" content="Randiv Dulneth, MrRandiv, BSc in IT Student, SLIIT, Aspiring Full-Stack Developer, DevOps Enthusiast, MERN Stack Developer, Web Development Student, React Developer, Node.js Developer, Express.js, MongoDB, Modern Web Applications, Student Portfolio, Web Development Projects, Software Development Student, ICT Student, Responsive Web Design, JavaScript Developer, Web Development Skills, University Projects" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://mr-randiv.vercel.app/" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://mr-randiv.vercel.app/" />
        <meta property="og:title" content="Randiv Dulneth (MrRandiv) | BSc in IT Student | Aspiring Full-Stack Developer & DevOps Enthusiast" />
        <meta property="og:description" content="BSc in IT student at SLIIT passionate about full-stack web development and DevOps. Building expertise in modern web technologies and cloud infrastructure." />
        <meta property="og:image" content="https://mr-randiv.vercel.app/images/Homeprofile-800x800.webp" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://mr-randiv.vercel.app/" />
        <meta name="twitter:title" content="Randiv Dulneth (MrRandiv) | BSc in IT Student | Aspiring Full-Stack Developer & DevOps Enthusiast" />
        <meta name="twitter:description" content="BSc in IT student at SLIIT passionate about full-stack web development and DevOps practices. Building expertise in modern web technologies and cloud infrastructure." />
        <meta name="twitter:image" content="https://mr-randiv.vercel.app/images/Homeprofile-800x800.webp" />
        <meta name="twitter:creator" content="@mrrandiv" />
      </Helmet>

      <StructuredData type="person" />
      <StructuredData type="website" />
      <ImageStructuredData type="all" />
      <ImageStructuredData type="single" imageKey="homeProfile" />

      <div className="min-h-screen pt-16 md:pt-0 bg-gray-50 dark:bg-secondary">
        {/* Hero Section - Modern UI/UX Design */}
        <section className="min-h-screen flex items-center bg-gray-50 dark:bg-secondary relative overflow-hidden pt-4 sm:pt-0">
          {/* Decorative Geometric Shapes */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {/* Top Left Circle */}
            <div className="absolute top-32 left-20 w-20 h-20 border-4 border-primary/30 rounded-full"></div>
            <div className="absolute top-36 left-24 w-12 h-12 border-4 border-accent/20 rounded-full"></div>

            {/* Top Right Plus */}
            <div className="absolute top-40 right-32 text-6xl text-primary/20 font-thin">+</div>

            {/* Bottom Left Triangle */}
            <div className="absolute bottom-40 left-40 w-0 h-0 border-l-[40px] border-l-transparent border-r-[40px] border-r-transparent border-b-[60px] border-b-primary/10"></div>

            {/* Wavy Line */}
            <svg className="absolute top-1/4 left-1/3 w-32 h-16 opacity-20" viewBox="0 0 100 50">
              <path d="M 0 25 Q 25 0, 50 25 T 100 25" stroke="#007BFF" strokeWidth="3" fill="none" />
            </svg>

            {/* Play Button Shape */}
            <div className="absolute bottom-1/3 left-1/2 w-0 h-0 border-t-[20px] border-t-transparent border-l-[35px] border-l-accent/20 border-b-[20px] border-b-transparent"></div>
          </div>

          <div className="container-custom relative z-10 px-4 md:px-8 lg:px-12 xl:px-0">
            <div className="grid lg:grid-cols-2 gap-8 md:gap-10 lg:gap-16 items-center py-12 md:py-16 lg:py-20">
              {/* Left Side - Content */}
              <div className="space-y-5 md:space-y-6 lg:space-y-8 opacity-100 translate-x-0">
                {/* Main Heading - Bold & Large */}
                <div className="space-y-3 md:space-y-4">
                  <div className="inline-flex items-center gap-2 bg-primary/10 backdrop-blur-sm border border-primary/30 rounded-full px-3 md:px-4 py-1.5 md:py-2 text-xs md:text-sm mb-3 md:mb-4">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
                    </span>
                    <span className="text-gray-700 dark:text-gray-300">Available for Work</span>
                  </div>

                  <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold font-heading leading-tight">
                    <span className="text-gray-900 dark:text-white">Hi, I'm</span>
                    <br />
                    <span className="gradient-text">Randiv Dulneth</span>
                  </h1>

                  <h2 className="text-lg md:text-xl lg:text-2xl xl:text-3xl text-gray-700 dark:text-gray-300 font-medium leading-relaxed">
                    BSc in IT Student | Aspiring Software Engineer | Full-Stack Developer & DevOps Enthusiast
                  </h2>
                </div>

                {/* Description */}
                <p className="text-sm md:text-base lg:text-lg text-gray-600 dark:text-gray-400 leading-relaxed max-w-xl">
                  I am a <span className="text-primary font-semibold">BSc in IT student at SLIIT</span> passionate about{' '}
                  <span className="text-primary font-semibold">full-stack web development</span> and{' '}
                  <span className="text-primary font-semibold">DevOps practices</span>. Through coursework and hands-on projects, 
                  I'm actively building my expertise in <span className="text-primary font-semibold">modern web technologies</span> and{' '}
                  <span className="text-primary font-semibold">cloud infrastructure</span>. I'm eager to apply my 
                  knowledge in real-world projects, contribute to innovative solutions, and continue growing as a developer.
                </p>

                {/* CTA Button - Large & Prominent */}
                <div className="pt-2 md:pt-4">
                  <Link
                    to="/projects"
                    className="inline-flex items-center gap-2 md:gap-3 bg-primary text-white px-6 md:px-8 py-3 md:py-4 rounded-xl font-semibold text-base md:text-lg transition-all duration-300 hover:bg-blue-600 hover:shadow-2xl hover:shadow-primary/50 hover:scale-105 group"
                  >
                    View My Projects
                    <ArrowRight className="group-hover:translate-x-1 transition-transform" size={20} />
                  </Link>
                </div>

                {/* Social Links */}
                <div className="flex gap-3 md:gap-4 pt-3 md:pt-4">
                  <a href="https://github.com/Randiv01" target="_blank" rel="noopener noreferrer" className="p-2.5 md:p-3 bg-white dark:bg-dark-100 rounded-xl border border-gray-200 dark:border-dark-300 hover:border-primary hover:scale-110 transition-all duration-300" aria-label="GitHub">
                    <Github size={20} className="md:w-[22px] md:h-[22px] text-gray-600 dark:text-gray-300 hover:text-primary transition-colors" />
                  </a>
                  <a href="https://linkedin.com/in/randiv-dulneth" target="_blank" rel="noopener noreferrer" className="p-2.5 md:p-3 bg-white dark:bg-dark-100 rounded-xl border border-gray-200 dark:border-dark-300 hover:border-primary hover:scale-110 transition-all duration-300" aria-label="LinkedIn">
                    <Linkedin size={20} className="md:w-[22px] md:h-[22px] text-gray-600 dark:text-gray-300 hover:text-primary transition-colors" />
                  </a>
                  <a href="https://www.instagram.com/randiv_xi?igsh=MW9lNW40cWFmaWZwMw%3D%3D&utm_source=qr" target="_blank" rel="noopener noreferrer" className="p-2.5 md:p-3 bg-white dark:bg-dark-100 rounded-xl border border-gray-200 dark:border-dark-300 hover:border-primary hover:scale-110 transition-all duration-300" aria-label="Instagram">
                    <Instagram size={20} className="md:w-[22px] md:h-[22px] text-gray-600 dark:text-gray-300 hover:text-primary transition-colors" />
                  </a>
                  <a href="mailto:randiv.dulneth01@gmail.com" className="p-2.5 md:p-3 bg-white dark:bg-dark-100 rounded-xl border border-gray-200 dark:border-dark-300 hover:border-primary hover:scale-110 transition-all duration-300" aria-label="Email">
                    <Mail size={20} className="md:w-[22px] md:h-[22px] text-gray-600 dark:text-gray-300 hover:text-primary transition-colors" />
                  </a>
                </div>
              </div>

              {/* Right Side - Tilted Image with Modern Design */}
              <div className="relative mt-8 md:mt-10 lg:mt-0 opacity-100 translate-x-0">
                <div className="relative flex items-center justify-center">
                  {/* Background Decorative Shape - Blue Blob */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-[280px] h-[350px] md:w-[350px] md:h-[420px] lg:w-[450px] lg:h-[550px] bg-primary rounded-[60px] transform rotate-12 opacity-90"></div>
                  </div>

                  {/* Image Container - Tilted Card */}
                  <div className="relative transform -rotate-6 hover:rotate-0 transition-all duration-500 group">
                    {/* White Border Frame */}
                    <div className="absolute -inset-3 md:-inset-4 bg-white rounded-[30px] md:rounded-[40px] shadow-2xl"></div>

                    {/* Main Image */}
                    <div className="relative bg-white rounded-[25px] md:rounded-[35px] overflow-hidden shadow-xl">
                      <img
                        src="/images/Homeprofile-800x800.webp"
                        alt="Randiv Dulneth (MrRandiv) - BSc in IT Student & Aspiring Full-Stack Developer | MERN Stack & DevOps Portfolio"
                        title="Randiv Dulneth - BSc in IT Student & Aspiring Developer"
                        className="w-[240px] h-[300px] md:w-[300px] md:h-[380px] lg:w-[380px] lg:h-[480px] object-cover transform group-hover:scale-105 transition-transform duration-700"
                        width="380"
                        height="480"
                        fetchpriority="high"
                        decoding="sync"
                      />
                    </div>
                  </div>

                  {/* Decorative Circle - Top Right */}
                  <div className="absolute -top-4 -right-4 md:-top-6 md:-right-6 lg:-top-8 lg:-right-8 w-16 h-16 md:w-20 md:h-20 lg:w-24 lg:h-24 border-4 border-accent rounded-full opacity-40"></div>

                  {/* Decorative Circle - Bottom Left */}
                  <div className="absolute -bottom-4 -left-4 md:-bottom-6 md:-left-6 lg:-bottom-8 lg:-left-8 w-14 h-14 md:w-16 md:h-16 lg:w-20 lg:h-20 border-4 border-primary rounded-full opacity-30"></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section ref={statsRef} className="section bg-white dark:bg-dark-100">
          <div className="container-custom">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {stats.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <div
                    key={index}
                    className={`card p-8 text-center group cursor-pointer hover-lift ${statsVisible ? 'animate-scale-in' : ''
                      }`}
                    style={{
                      animationDelay: `${index * 0.15}s`,
                      opacity: statsVisible ? 1 : 0
                    }}
                  >
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4 group-hover:scale-110 transition-transform duration-300">
                      <Icon size={32} className="text-primary" />
                    </div>
                    <h3 className="text-4xl font-bold font-heading mb-2 gradient-text">{stat.value}</h3>
                    <p className="text-gray-600 dark:text-gray-400">{stat.label}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Tech Stack Section */}
        <section ref={techRef} className="section bg-gray-50 dark:bg-secondary">
          <div className="container-custom">
            <div className={`text-center mb-12 ${techVisible ? 'animate-fade-in-up' : ''}`} style={{ opacity: techVisible ? 1 : 0 }}>
              <h2 className="text-3xl md:text-4xl font-bold font-heading mb-3">
                <span className="text-gray-900 dark:text-white">My</span>{' '}
                <span className="gradient-text">Tech Stack</span>
              </h2>
              <p className="text-gray-600 dark:text-gray-400">Technologies I work with to build amazing products</p>
            </div>
            {/* Development Stack */}
            <div className="mb-12">
              <h3 className="text-2xl md:text-3xl font-bold font-heading mb-6 text-center text-gray-800 dark:text-gray-200">
                Development <span className="text-primary">Stack</span>
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-6 max-w-6xl mx-auto">
                {developmentStack.map((tech, index) => (
                  <div
                    key={index}
                    className={`bg-white dark:bg-dark-100 border border-gray-200 dark:border-dark-300 rounded-2xl p-6 text-center hover:border-primary hover:shadow-lg hover:shadow-primary/20 transition-all duration-300 hover:scale-105 group relative overflow-hidden hover-lift ${techVisible ? 'animate-fade-in-up' : ''
                      }`}
                    style={{
                      animationDelay: `${index * 0.1}s`,
                      opacity: techVisible ? 1 : 0
                    }}
                  >
                    <div className={`absolute inset-0 bg-gradient-to-br ${tech.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}></div>
                    <div className="relative mb-3 flex items-center justify-center h-16 transform group-hover:scale-110 transition-transform duration-300">
                      {tech.iconImage ? (
                        <img src={tech.iconImage} alt={tech.name} className="w-14 h-14 object-contain" width="56" height="56" loading="lazy" />
                      ) : (
                        <span className="text-5xl">{tech.icon}</span>
                      )}
                    </div>
                    <p className="relative text-gray-900 dark:text-white font-semibold text-base group-hover:text-primary transition-colors">
                      {tech.name}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* DevOps & Cloud Stack */}
            <div>
              <h3 className="text-2xl md:text-3xl font-bold font-heading mb-6 text-center text-gray-800 dark:text-gray-200">
                DevOps & <span className="text-primary">Cloud</span>
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-6 max-w-6xl mx-auto">
                {devopsStack.map((tech, index) => (
                  <div
                    key={index}
                    className={`bg-white dark:bg-dark-100 border border-gray-200 dark:border-dark-300 rounded-2xl p-6 text-center hover:border-primary hover:shadow-lg hover:shadow-primary/20 transition-all duration-300 hover:scale-105 group relative overflow-hidden hover-lift ${techVisible ? 'animate-fade-in-up' : ''
                      }`}
                    style={{
                      animationDelay: `${(index + developmentStack.length) * 0.05}s`,
                      opacity: techVisible ? 1 : 0
                    }}
                  >
                    <div className={`absolute inset-0 bg-gradient-to-br ${tech.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}></div>
                    <div className="relative mb-3 flex items-center justify-center h-16 transform group-hover:scale-110 transition-transform duration-300">
                      {tech.iconImage ? (
                        <img src={tech.iconImage} alt={tech.name} className="w-14 h-14 object-contain" width="56" height="56" loading="lazy" />
                      ) : (
                        <span className="text-5xl">{tech.icon}</span>
                      )}
                    </div>
                    <p className="relative text-gray-900 dark:text-white font-semibold text-base group-hover:text-primary transition-colors">
                      {tech.name}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section ref={featuresRef} className="section bg-white dark:bg-dark-100">
          <div className="container-custom">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div
                    key={index}
                    className={`bg-gray-50 dark:bg-secondary border-2 border-gray-200 dark:border-dark-300 rounded-2xl p-8 hover:border-primary transition-all duration-300 group hover-lift ${featuresVisible ? 'animate-fade-in-up' : ''
                      }`}
                    style={{
                      animationDelay: `${index * 0.15}s`,
                      opacity: featuresVisible ? 1 : 0
                    }}
                  >
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-2xl mb-6 group-hover:bg-primary/20 transition-colors">
                      <Icon size={32} className="text-primary" />
                    </div>
                    <h3 className="text-2xl font-bold font-heading mb-4 text-gray-900 dark:text-white">{feature.title}</h3>
                    <p className="text-gray-600 dark:text-gray-400 leading-relaxed">{feature.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="section bg-gradient-to-r from-primary to-accent">
          <div className="container-custom text-center">
            <h2 className="text-3xl md:text-4xl font-bold font-heading mb-6 text-white">
              Looking for a Passionate Developer?
            </h2>
            <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
              Always eager to learn, build, and grow with cutting-edge technology.
            </p>
            <Link
              to="/contact"
              className="inline-block bg-white text-primary px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-all duration-300 hover:scale-105 shadow-lg"
            >
              Let's Connect
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}

export default Home;